import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { userService } from '../../_services';
import ValidationErrorToast from '../../components/validationErrorToast';

interface Customer {
    id: string;
    customerName: string;
    organizationName: string;
    organizationId: string | null;
    outstandingInvoices: number;
    amountCollected: number;
    callsToday: number;
    paymentStatus: string;
    connectionStatus: string;
    connectionType: string;
    connectionDisplay: string;
    isActive: boolean;
    createdAt: string | null;
}

const CBACustomers = () => {
    const [customers, setCustomers] = useState<Customer[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const whitelabelDomain = window.location.hostname;

    useEffect(() => {
        loadCustomers();
    }, []);

    const loadCustomers = async () => {
        setIsLoading(true);
        try {
            const response = await userService.aiRequest({
                method: 'get-whitelabel-customers',
                data: { whitelabel_domain: whitelabelDomain }
            }, true);

            if (response.data?.customers) {
                setCustomers(response.data.customers);
            }
        } catch (error) {
            console.error('Failed to load customers:', error);
            ValidationErrorToast('Error', 'Failed to load customers');
        } finally {
            setIsLoading(false);
        }
    };

    // 🎯 FIXED: This function now properly constructs the Flask backend URL
    const handleLoginAsUser = (customerId: string, organizationId: string | null) => {
        // Determine the Flask backend URL
        const flaskBackendUrl = window.location.hostname === 'localhost' 
            ? 'http://localhost:5000'  // Development
            : 'https://ai.mploi.com';   // Production
        
        // Construct the URL to the Flask route that generates the token
        const loginUrl = `${flaskBackendUrl}/admin/login-as-user/${customerId}/${organizationId || 'none'}`;
        
        console.log('Opening admin login URL:', loginUrl);
        
        // Open in new tab - this will hit Flask, generate token, and redirect to /admin-login?token=xyz
        window.open(loginUrl, '_blank');
    };

    const styles: { [key: string]: React.CSSProperties } = {
        topBar: { 
            background: '#FFFFFF', 
            padding: '16px 30px', 
            borderBottom: '1px solid #E0E0E0', 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center' 
        },
        topBarTitle: { 
            color: '#141414', 
            margin: 0, 
            fontSize: '22px', 
            fontWeight: '700' 
        },
        main: { 
            flex: 1, 
            overflowY: 'auto', 
            padding: '24px 30px', 
            backgroundColor: '#F8F9FA' 
        },
        card: { 
            background: '#FFFFFF', 
            borderRadius: '12px', 
            border: '1px solid #E0E0E0', 
            overflow: 'hidden' 
        },
        table: { 
            width: '100%', 
            borderCollapse: 'collapse' 
        },
        th: {
            padding: '12px 16px',
            textAlign: 'left',
            borderBottom: '1px solid #E0E0E0',
            backgroundColor: '#F8F9FA',
            color: '#555555',
            fontSize: '12px',
            fontWeight: '600',
            textTransform: 'uppercase',
        },
        td: {
            padding: '14px 16px',
            textAlign: 'left',
            borderBottom: '1px solid #E0E0E0',
            color: '#242424',
            fontSize: '14px',
            verticalAlign: 'middle',
        },
        tr: {
            transition: 'background-color 0.2s ease',
        },
        actionButtonsContainer: {
            display: 'flex',
            gap: '8px',
        },
        actionButton: {
            padding: '6px 12px',
            borderRadius: '6px',
            border: '1px solid #0065FF',
            cursor: 'pointer',
            backgroundColor: '#FFFFFF',
            color: '#0065FF',
            fontSize: '13px',
            fontWeight: '600',
            transition: 'all 0.2s ease',
            textDecoration: 'none',
            display: 'inline-block',
        },
        button: { 
            padding: '8px 16px', 
            borderRadius: '8px', 
            border: 'none', 
            cursor: 'pointer', 
            display: 'inline-flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            gap: '8px', 
            fontSize: '14px', 
            fontWeight: '600', 
            transition: 'all 0.2s ease',
            textDecoration: 'none',
        },
        primaryButton: { 
            backgroundColor: '#0065FF', 
            color: '#FFFFFF' 
        },
        statusPill: {
            display: 'inline-block',
            padding: '4px 10px',
            borderRadius: '12px',
            fontSize: '12px',
            fontWeight: '500',
            lineHeight: '1.2',
        },
        statusPaid: { backgroundColor: '#E6F7F0', color: '#0A7A4F' },
        statusTrial: { backgroundColor: '#FEF3E2', color: '#B54708' },
        statusExpired: { backgroundColor: '#FFEBEB', color: '#D92D20' },
        statusDisabled: { backgroundColor: '#F2F4F7', color: '#344054' },
        connectionBadge: {
            display: 'inline-block',
            padding: '3px 8px',
            borderRadius: '4px',
            fontSize: '11px',
            fontWeight: '600',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
        },
        connectionConnected: { backgroundColor: '#E6F7F0', color: '#0A7A4F' },
        connectionNotConnected: { backgroundColor: '#FEF3E2', color: '#B54708' },
        loadingContainer: {
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '400px',
        },
        emptyState: {
            textAlign: 'center',
            padding: '60px 20px',
            color: '#666666',
        },
    };

    const getStatusStyle = (status: string) => {
        switch (status) {
            case 'Paid (Monthly)':
            case 'Paid (Annual)':
                return { ...styles.statusPill, ...styles.statusPaid };
            case 'Trial':
                return { ...styles.statusPill, ...styles.statusTrial };
            case 'Expired':
                return { ...styles.statusPill, ...styles.statusExpired };
            case 'Disabled':
                return { ...styles.statusPill, ...styles.statusDisabled };
            default:
                return styles.statusPill;
        }
    };

    const getConnectionBadgeStyle = (connectionDisplay: string) => {
        if (connectionDisplay === 'Not Connected') {
            return { ...styles.connectionBadge, ...styles.connectionNotConnected };
        }
        return { ...styles.connectionBadge, ...styles.connectionConnected };
    };

    const TableRow = ({ children }) => {
        const [hover, setHover] = useState(false);
        return (
            <tr 
                style={{ ...styles.tr, ...(hover ? { backgroundColor: '#F9FAFC' } : {})}} 
                onMouseEnter={() => setHover(true)} 
                onMouseLeave={() => setHover(false)}
            >
                {children}
            </tr>
        );
    };

    if (isLoading) {
        return (
            <>
                <Helmet><title>Customers</title></Helmet>
                <div style={styles.topBar}>
                    <h1 style={styles.topBarTitle}>Customer Management</h1>
                </div>
                <main style={styles.main}>
                    <div style={styles.loadingContainer}>
                        <div className="spinner-border" style={{ width: '3rem', height: '3rem', color: '#0065FF' }} role="status">
                            <span className="sr-only">Loading...</span>
                        </div>
                    </div>
                </main>
            </>
        );
    }

    return (
        <>
            <Helmet><title>Customers</title></Helmet>
            <div style={styles.topBar}>
                <h1 style={styles.topBarTitle}>Customer Management</h1>
                <Link 
                    to="/accounts/signup" 
                    style={{...styles.button, ...styles.primaryButton}}
                >
                    Add Customer
                </Link>
            </div>
            <main style={styles.main}>
                {customers.length === 0 ? (
                    <div style={styles.card}>
                        <div style={styles.emptyState}>
                            <h3>No customers found</h3>
                            <p>Get started by adding your first customer</p>
                            <Link 
                                to="/accounts/signup" 
                                style={{...styles.button, ...styles.primaryButton, marginTop: '16px'}}
                            >
                                Add Customer
                            </Link>
                        </div>
                    </div>
                ) : (
                    <div style={styles.card}>
                        <table style={styles.table}>
                            <thead>
                                <tr>
                                    <th style={styles.th}>Customer Name</th>
                                    <th style={styles.th}>Organization Name</th>
                                    <th style={styles.th}>Connection</th>
                                    <th style={styles.th}>Payment Status</th>
                                    <th style={styles.th}>Outstanding Invoices</th>
                                    <th style={styles.th}>Amount Collected</th>
                                    <th style={styles.th}>Calls Today</th>
                                    <th style={styles.th}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {customers.map((customer) => (
                                    <TableRow key={customer.id}>
                                        <td style={styles.td}>{customer.customerName}</td>
                                        <td style={styles.td}>{customer.organizationName}</td>
                                        <td style={styles.td}>
                                            <span style={getConnectionBadgeStyle(customer.connectionDisplay)}>
                                                {customer.connectionDisplay}
                                            </span>
                                        </td>
                                        <td style={styles.td}>
                                            <span style={getStatusStyle(customer.paymentStatus)}>
                                                {customer.paymentStatus}
                                            </span>
                                        </td>
                                        <td style={styles.td}>{customer.outstandingInvoices}</td>
                                        <td style={styles.td}>
                                            ${customer.amountCollected.toLocaleString(undefined, { 
                                                minimumFractionDigits: 2, 
                                                maximumFractionDigits: 2 
                                            })}
                                        </td>
                                        <td style={styles.td}>{customer.callsToday}</td>
                                        <td style={styles.td}>
                                            <div style={styles.actionButtonsContainer}>
                                                {/* 🎯 FIXED: Now uses onClick handler instead of href */}
                                                <button
                                                    onClick={() => handleLoginAsUser(customer.id, customer.organizationId)}
                                                    style={styles.actionButton}
                                                >
                                                    Access customer account
                                                </button>
                                            </div>
                                        </td>
                                    </TableRow>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </main>
        </>
    );
};

export default CBACustomers;
